import './index.css'

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import MTable from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Typography from '@mui/material/Typography'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React from 'react'
import { Link } from 'react-router-dom';
import { fetchAllListRows } from 'src/api'
import ModalComponent from 'src/components/common/modalComponent'
import { exportTableToCsv } from 'src/util/csv'

import TablePaginationActions from './actions'
import { getAllLeafColumnDefs, getColumnId } from './column-utils'

export function LocalTable({
  columns,
  name,
  rowActions = true,
  setRowSelectionData,
  addChildActionBtnTitle,
  addChildActionBtnClick,
  viewChildActionBtnTitle,
  viewChildActionBtnUri,
  viewActionBtnTitle,
  onViewActionBtnClick,
  editActionBtnTitle = 'Edit',
  deleteActionBtnTitle = 'Delete',
  exportApiName,
  exportFileName,
  toolbarExtra,
  ...props
}) {
  const [selectedRecord, setSelectedRecord] = React.useState(null)
  const [rowSelection, setRowSelection] = React.useState({})
  const [isExporting, setIsExporting] = React.useState(false)
  const [exportError, setExportError] = React.useState('')

  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const handlePaginationChange = value => {
    props?.setPagination(value)
    setPagination(value)
  }

  const rows = React.useMemo(() => props.data?.docs || [], [props.data?.docs])

  // the row-action menu is shown when any of its actions is configured
  const hasRowActions = Boolean(
    props.onEditClick || props.onDeleteClick || onViewActionBtnClick
  )

  const table_data = React.useMemo(
    () =>
      props.isFetchingData && !rows?.length
        ? [...Array(10).fill(null)].map(() =>
          Object.assign(
            {},
            ...getAllLeafColumnDefs(columns).map(col => ({
              [getColumnId(col)]: null,
            }))
          )
        )
        : rows,
    [columns, rows, props.isFetchingData]
  )

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )

  const table = useReactTable({
    data: table_data,
    pageCount: props?.data?.totalPages ?? -1,
    columns,
    state: {
      pagination,
      rowSelection,
    },
    getRowId: (data, index) => data._id || index,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: handlePaginationChange,
    manualPagination: true,
    // Pipeline
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel()
  })

  // function for edit table data
  const handleEditClick = record => {
    props.onEditClick(record)
  }

  // function for delete confirmation
  const handleDeleteClick = record => {
    setSelectedRecord(record)
  }

  // function for delete table data
  const handleDeleteConfirm = async () => {
    await props.onDeleteClick(selectedRecord?.id)
    setSelectedRecord(null)
  }

  // download every record of this module (all pages) as a CSV file
  const handleExportCsv = async () => {
    setExportError('')
    setIsExporting(true)
    try {
      const allRows = await fetchAllListRows(exportApiName)
      exportTableToCsv(exportFileName || `${name || exportApiName}.csv`, columns, allRows)
    } catch (err) {
      setExportError('Could not export data. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  React.useEffect(() => {
    if (setRowSelectionData && table.getSelectedRowModel().flatRows.length) {
      setRowSelectionData(table.getSelectedRowModel().flatRows)
    }
  }, [table.getSelectedRowModel().flatRows])

  return (
    <Box sx={{ width: '100%' }}>
      {(exportApiName || toolbarExtra) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 1 }}>
          {exportError && (
            <Typography variant="caption" color="error">
              {exportError}
            </Typography>
          )}
          {toolbarExtra}
          {exportApiName && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
              disabled={isExporting || !rows.length}
            >
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          )}
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          overflowX: "auto",
          minHeight: 300,
        }}
      >
        <MTable sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow
                key={headerGroup.id}
                sx={{
                  backgroundColor: "primary.main",
                  "& th": {
                    color: "primary.contrastText",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                {hasRowActions ? (
                  <TableCell width="30" key="edit_cell">Action</TableCell>
                ) : null}
                {headerGroup.headers.map(header => {
                  return (
                    <TableCell
                      key={header.id}
                      colSpan={header.colSpan}
                      sx={{
                        minWidth: header.column.columnDef.width,
                        width: header.column.columnDef.width,
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          }}
                        >
                          <TableSortLabel
                            active={!!header.column.getIsSorted()}
                            direction={header.column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
                            onClick={header.column.getToggleSortingHandler()}
                            hideSortIcon={!header.column.getIsSorted()}
                            sx={{
                              color: "#fff !important",
                              "&:hover": {
                                color: "#dacdcdff !important",
                              },
                              "&.Mui-active": {
                                color: "#fff !important",
                              },
                              "&.Mui-active .MuiTableSortLabel-icon": {
                                color: "#fff !important",
                              },
                              whiteSpace: "nowrap"
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </TableSortLabel>
                        </Box>
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {!props.isLoading && !props.isFetching && rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={getAllLeafColumnDefs(columns).length + (hasRowActions ? 1 : 0)}
                  align="center"
                  sx={{ py: 8, border: 0 }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Data not available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.map(row => {
              return (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#e7f1f8ff",
                    },
                  }}
                >
                  {rowActions && (
                    <EditCell
                      record={row}
                      onEditClick={props.onEditClick ? handleEditClick : undefined}
                      onDeleteClick={props.onDeleteClick ? handleDeleteClick : undefined}
                      addChildActionBtnTitle={addChildActionBtnTitle}
                      addChildActionBtnClick={addChildActionBtnClick}
                      viewChildActionBtnTitle={viewChildActionBtnTitle}
                      viewChildActionBtnUri={viewChildActionBtnUri}
                      viewActionBtnTitle={viewActionBtnTitle}
                      onViewActionBtnClick={onViewActionBtnClick}
                      editActionBtnTitle={editActionBtnTitle}
                      deleteActionBtnTitle={deleteActionBtnTitle}
                    />
                  )}
                  {row.getVisibleCells().map(cell => {
                    return (
                      <TableCell
                        key={cell.id}
                        sx={{
                          minWidth: cell.column.columnDef.width,
                          width: cell.column.columnDef.width,
                        }}
                      >
                        {!props.isFetchingData ? (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        ) : (
                          <Skeleton
                            variant="text"
                            animation="wave"
                            height={20}
                          />
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </MTable>
      </TableContainer>
      {!props?.isLoading && (
        <TablePagination
          rowsPerPageOptions={[
            10,
            20,
            50,
            100,
          ]}
          component="div"
          count={props?.data?.totalDocs ?? 0}
          rowsPerPage={pageSize}
          page={pageIndex}
          disableNextButton={!table.getCanNextPage()}
          disablePrevButton={!table.getCanPreviousPage()}
          SelectProps={{
            inputProps: { 'aria-label': 'rows per page' },
            native: false, // Switch to Material-UI Select instead of native select
            MenuProps: {
              anchorOrigin: {
                vertical: 'top', // Makes the dropdown open upwards
                horizontal: 'center',
              },
              transformOrigin: {
                vertical: 'bottom',
                horizontal: 'center',
              },
            },
          }}

          onPageChange={(_, page) => {
            table.setPageIndex(page)
            setPagination(prev => ({ ...prev, pageIndex: page }))
          }}
          onRowsPerPageChange={(e) => {
            const size = e.target.value ? Number(e.target.value) : 10
            const totalDocs = props?.data?.totalDocs ?? 0
            const maxPageIndex = Math.ceil(totalDocs / size) - 1

            // If we're on the last page with current page size, stay on the last page
            const isCurrentlyOnLastPage = pageIndex >= Math.ceil(totalDocs / pageSize) - 1
            const newPageIndex = isCurrentlyOnLastPage ? maxPageIndex : Math.min(pageIndex, maxPageIndex)

            const newPagination = {
              pageIndex: newPageIndex,
              pageSize: size
            }

            setPagination(newPagination)
            handlePaginationChange(newPagination)
          }}
          ActionsComponent={TablePaginationActions}
        />

      )}
      {selectedRecord && <ModalComponent
        open={selectedRecord}
        onClose={() => setSelectedRecord(null)}
        onConfirm={() => {
          setSelectedRecord(null);
          handleDeleteConfirm();
        }}
        title={`Are you sure, you want to delete this ${name}?`}
        confirmBtn="YES, Delete"
      />}
    </Box>
  )
}

function EditCell({
  onEditClick,
  onDeleteClick,
  record,
  addChildActionBtnTitle,
  addChildActionBtnClick,
  viewChildActionBtnTitle,
  viewChildActionBtnUri,
  viewActionBtnTitle,
  onViewActionBtnClick,
  editActionBtnTitle,
  deleteActionBtnTitle,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)

  if (!onEditClick && !onDeleteClick && !onViewActionBtnClick) return null

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleEditClick = e => {
    e.stopPropagation()
    onEditClick(record.original)
    handleClose()
  }

  const handleDeleteClick = e => {
    e.stopPropagation()
    onDeleteClick(record.original)
    handleClose()
  }

  const handleViewClick = e => {
    e.stopPropagation()
    onViewActionBtnClick(record.original)
    handleClose()
  }

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const actionBtnStyle = {
    "&:hover": {
      backgroundColor: "primary.main",
      "& svg": { color: "#fff" },
      "& .MuiTypography-root": { color: "#fff" },
    },
    transition: "all 0.3s ease",
  }

  return (
    <TableCell>
      <IconButton
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {viewActionBtnTitle && onViewActionBtnClick ? (
          <MenuItem
            sx={actionBtnStyle}
          >
            <IconButton onClick={handleViewClick}>
              <RemoveRedEyeIcon sx={{ color: "#0288d1" }} />
              <Typography sx={{ ml: 1 }}>{viewActionBtnTitle}</Typography>
            </IconButton>
          </MenuItem>
        ) : null}

        {onEditClick ? (
          <MenuItem
            sx={actionBtnStyle}
          >
            <IconButton onClick={handleEditClick}>
              <EditIcon sx={{ color: "#1976d2" }} />
              <Typography sx={{ ml: 1 }}>{editActionBtnTitle}</Typography>
            </IconButton>
          </MenuItem>
        ) : null}

        {addChildActionBtnTitle && addChildActionBtnClick ? (
          <MenuItem
            sx={actionBtnStyle}
          >
            <IconButton
              onClick={(e) => {
                addChildActionBtnClick();
                handleEditClick(e);
              }}
            >
              <AddIcon sx={{ color: "#2e7d32" }} />
              <Typography sx={{ ml: 1 }}>{addChildActionBtnTitle}</Typography>
            </IconButton>
          </MenuItem>
        ) : null}

        {viewChildActionBtnTitle && viewChildActionBtnUri ? (
          <Link to={`${viewChildActionBtnUri}${record?.original?._id}`} style={{ textDecoration: "none" }}>
            <MenuItem
              sx={actionBtnStyle}
            >
              <IconButton>
                <VisibilityIcon sx={{ color: "#388e3c" }} />
                <Typography sx={{ ml: 1, transition: "color 0.3s ease" }}>
                  {viewChildActionBtnTitle}
                </Typography>
              </IconButton>
            </MenuItem>
          </Link>
        ) : null}

        {onDeleteClick ? (
          <MenuItem
            sx={actionBtnStyle}
          >
            <IconButton onClick={handleDeleteClick}>
              <DeleteIcon sx={{ color: "#d32f2f" }} />
              <Typography sx={{ ml: 1 }}>{deleteActionBtnTitle}</Typography>
            </IconButton>
          </MenuItem>
        ) : null}
      </Menu>
    </TableCell>

  )
}
